import { Component, computed, signal } from '@angular/core';
import { GroupedMenuItems } from '../../shared/components/dropdown-menu/dropdown-menu.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModalService } from '../../core/services/modal.service';
import {
    OrganizationPickerComponent,
} from '../../core/components/organizations/organization-picker/organization-picker.component';
import { AppService } from '../../core/services/app.service';
import { SubHeaderComponent, SubHeaderItem } from '../sub-header/sub-header.component';
import { map } from 'rxjs/operators';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CurrentRoute } from '@knalgeel/pandora';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import { DropdownDirective } from '../../shared/directives/dropdown.directive';
import { AvatarComponent } from '../../shared/components/avatar/avatar.component';
import { RouterLink, RouterLinkActive } from '@angular/router';
import {
    LocationSwitcherComponent,
} from '../../core/components/location-switcher/location-switcher.component';
import { CdnAssetUrl } from '../../../../graphql/generated';
import { CustomAuthService } from '../../core/services/custom-auth.service';
import { NgIcon } from '@ng-icons/core';
import { MemberService } from '../../core/services/member.service';
import { TeamCaptainService } from '../../core/services/team-captain.service';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import {
    LucideBanknote,
    LucideBuilding2,
    LucideCalendarDays,
    LucideClock,
    LucideFileMinus,
    LucideGraduationCap,
    LucideHandshake,
    LucideIcon,
    LucideLandmark,
    LucideLayoutDashboard,
    LucideLogOut,
    LucideSettings,
    LucideTrophy,
    LucideUser,
    LucideUsers,
} from '@lucide/angular';
import { IconComponent } from '../../shared/components/icon/icon.component';

interface NavItem {
    label: string;
    href: string[];
    icon: LucideIcon;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    imports: [LocationSwitcherComponent, RouterLink, AvatarComponent, DropdownDirective, BreadcrumbsComponent, SubHeaderComponent, RouterLinkActive, NgIcon, NotificationBellComponent, IconComponent],
})
export class HeaderComponent {

    // Mobile menu state
    mobileMenuOpen = signal(false);

    user = toSignal(this.authService.user$);
    isAuthenticated = toSignal(this.authService.isAuthenticated$);
    isAgent = toSignal(this.memberService.isAgent$);
    isAdminOrOwner = toSignal(this.appContextService.isAdminOrOwner$);
    isActiveBonusCaptain = toSignal(this.teamCaptainService.myActiveBonusTeam$.pipe(map(team => !!team)));
    canCreatePlanning = toSignal(this.memberService.canCreatePlanning$);

    protected readonly organization = toSignal(this.appContextService.organization$);
    protected readonly establishment = toSignal(this.appContextService.establishment$);

    /**
     * A backoffice employee has no sales/planning of their own, and reports their own hours
     * instead of the fixed-block availability toggle — see agent-backoffice-schedule-page.
     */
    protected readonly isBackofficeEmployee = computed(() => !!this.establishment()?.isBackofficeEstablishment);

    submenu = toSignal<SubHeaderItem[]>(
        this.currentRoute.navigated$.pipe(
            map(route => route.snapshot.data['submenu'] || []),
        ),
    );

    breadcrumbs = toSignal(this.breadcrumbService.breadcrumbs$);

    navItems = computed(() => {
        if (this.isAgent()) {
            const backoffice = this.isBackofficeEmployee();
            const items: NavItem[] = [
                { label: 'Dashboard', href: ['my', 'dashboard'], icon: LucideLayoutDashboard },
                {
                    label: 'Rooster',
                    href: backoffice ? ['my', 'backoffice-schedule'] : ['my', 'schedule'],
                    icon: LucideCalendarDays,
                },
                { label: 'Salaris', href: ['my', 'salary'], icon: LucideBanknote },
                { label: 'Leaderboard', href: ['my', 'leaderboard'], icon: LucideTrophy },
                { label: 'Trainingen', href: ['my', 'trainingen'], icon: LucideGraduationCap },
                { label: 'Mijn Shifts', href: ['my', 'shifts'], icon: LucideClock },
            ];
            if (!backoffice) {
                items.splice(5, 0, { label: 'Mijn Sales', href: ['my', 'sales'], icon: LucideHandshake });
                if (this.isActiveBonusCaptain()) {
                    items.push({ label: 'Mijn Team', href: ['my', 'team-sales'], icon: LucideUsers });
                }
                if (this.canCreatePlanning()) {
                    items.push({ label: 'Planning', href: ['my', 'planning'], icon: LucideCalendarDays });
                }
            }
            return items;
        }
        return [
            { label: 'Dashboard', href: ['establishment', 'dashboard'], icon: LucideLayoutDashboard },
            { label: 'Planning', href: ['establishment', 'planning'], icon: LucideCalendarDays },
            { label: 'Medewerkers', href: ['establishment', 'employees'], icon: LucideUsers },
            { label: 'Sales', href: ['establishment', 'sales'], icon: LucideHandshake },
            { label: 'Uitbetalingen', href: ['establishment', 'fees'], icon: LucideBanknote },
            { label: 'Financieel', href: ['establishment', 'financieel'], icon: LucideLandmark },
            { label: 'Afboekingen', href: ['afboekingen'], icon: LucideFileMinus },
        ];
    });

    dropdownMenu = computed<GroupedMenuItems[]>(() => {
        if (this.isAgent()) {
            return [
                {
                    group: 'account',
                    items: [
                        {
                            label: 'Profiel',
                            icon: LucideUser,
                            href: ['/my', 'profile'],
                        },
                        {
                            label: 'Uitloggen',
                            icon: LucideLogOut,
                            execute: () => this.logout(),
                        },
                    ],
                },
            ];
        }
        return [
            {
                group: 'organization',
                items: [
                    {
                        label: this.organization()?.name,
                        icon: LucideBuilding2,
                        bold: true,
                        execute: () => this.openOrganizationPickerModal(),
                    },
                ],
            },
            {
                group: 'settings',
                items: [
                    {
                        label: 'Organisatieinstellingen',
                        icon: LucideSettings,
                        href: ['settings'],
                    },
                    {
                        label: 'Vestigingsinstellingen',
                        icon: LucideSettings,
                        href: ['settings', 'establishment'],
                    },
                ],
            },
            {
                group: 'account',
                items: [
                    {
                        label: 'Profiel',
                        icon: LucideUser,
                        href: '/profile',
                    },
                    {
                        label: 'Uitloggen',
                        icon: LucideLogOut,
                        execute: () => this.logout(),
                    },
                ],
            },
        ];
    });

    avatarProfile = computed<{
        avatar?: CdnAssetUrl;
        firstName: string;
        lastName: string;
    }>(() => {
        const email = this.user()?.email ?? '';
        // Use email prefix as name for now
        const emailPrefix = email.split('@')[0];
        const parts = emailPrefix.split('.');

        return {
            firstName: parts[0] ?? 'User',
            lastName: parts[1] ?? '',
        };
    });

    constructor(
        private readonly breadcrumbService: BreadcrumbService,
        private readonly currentRoute: CurrentRoute,
        private readonly modalService: ModalService,
        private readonly appContextService: AppService,
        private readonly authService: CustomAuthService,
        private readonly memberService: MemberService,
        private readonly teamCaptainService: TeamCaptainService,
    ) {
    }

    openOrganizationPickerModal() {
        this.modalService.push(OrganizationPickerComponent, {});
    }

    login() {
        // Custom auth login - redirect to login page
        window.location.href = '/login';
    }

    logout() {
        this.authService.logout().subscribe(() => {
            window.location.href = '/';
        });
    }

}
